// uploadProposal.jsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import { updateLegalTranslation } from "services/legalTranslationService";
import { updateLegislativeDrafting } from "services/legislativeService";
import { updateSectorDirective } from "services/sectorDirectives";
import { LEGAL_ADVISORY_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { useTranslation } from "react-i18next";

interface uploadProposalProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
    type?: string;
    onUploadProposal: (data: {
        proposalSummary: string;
        proposalFile: string;
    }) => void;
}

const UploadDraftLaw: React.FC<uploadProposalProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onUploadProposal,
    onClose,
    type,
}) => {
    const { t } = useTranslation();
    const [proposalSummary, setProposalSummary] = useState(request.translated_summary || "");
    const [proposalFile, setProposalFile] = useState(
        new Date().toISOString().split("T")[0]
    );
    const contextData = useAuth();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Create a ref for the modal content div
    const modalContentRef = useRef<HTMLDivElement>(null);

    // Effect to handle clicks outside the modal
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                modalContentRef.current &&
                !modalContentRef.current.contains(event.target as Node)
            ) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setLegalResearch((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setValidationError(null);
        setSubmitting(true);

        try {
            const data = new FormData();
            let response: any;
            if (type === "legal") {
                data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.draft_submitted.value);
                data.append("legal_request_advice_summary", proposalSummary);
                data.append("legal_request_advice_file", proposalFile);
                response = await updateLegalAdvisory(request.id, data);
            }
            else if (type === "sector_directives") {
                data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.draft_submitted.value);
                data.append("summary_of_comments", proposalSummary);
                data.append("written_comments_file", proposalFile);
                response = await updateSectorDirective(request.id, data);
            }
            else if (type === "legal_translation") {
                data.append("status", LEGAL_ADVISORY_REQUEST_STATUS.draft_submitted.value);
                data.append("translated_summary", proposalSummary);
                data.append("draft_translation_files", proposalFile);
                response = await updateLegalTranslation(request.id, data);
            }
            else {
                data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.draft_submitted.value);
                data.append("draft_summary", proposalSummary);
                data.append("draft_law_file", proposalFile);
                response = await updateLegislativeDrafting(request.id, data);
            }
            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(t("DraftLawUploaded") || "Draft Law Uploaded", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUploadDraftLaw") || "Failed to upload Draft Law.", "error");
        } finally {
            setSubmitting(false);
        }

        onUploadProposal({ proposalSummary, proposalFile });
        onClose();
    };

    const handleFileChange = (
        file: any,
    ) => {
        setProposalFile(file);
    };

    // Get title based on type
    const getTitle = () => {
        if (type === "legal" || type === "sector_directives") {
            return t("SummaryOfWrittenComments") || "Summary of Written Comments";
        } else if (type === "legal_translation") {
            return t("DraftLegalTranslation") || "Draft Legal Translation";
        }
        return t("DraftLaw") || "Draft Law";
    };

    // Get label based on type
    const getLabel = () => {
        if (type === "legal" || type === "sector_directives") {
            return t("WrittenComments") || "Written Comments";
        } else if (type === "legal_translation") {
            return t("TranslationSummary") || "Translation Summary";
        }
        return t("DraftLawFile") || "Draft Law File";
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {getTitle()}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        <div className="mb-4">
                            <label
                                htmlFor="remark"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {t("Summary") || "Summary"}
                            </label>
                            <textarea
                                id="remark"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere") || "detail here..."}
                                value={proposalSummary}
                                onChange={(e) => setProposalSummary(e.target.value)}
                            ></textarea>
                        </div>

                        <div className="mb-6">
                            <label
                                htmlFor="deadline"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {getLabel()}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>
                    </div>
                    {validationError && (
                        <p className="text-red-600 text-sm mb-4">{validationError}</p>
                    )}
                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md disabled:opacity-70"
                        >
                            {submitting ? (
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                                    </svg>
                                    {t("Submitting") || "Submitting..."}
                                </div>
                            ) : (
                                t("Submit") || "Submit"
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UploadDraftLaw;